import type { FieldError } from 'react-hook-form'

import { MoodysRating, SPRating } from '@moment/trading-types/reference-data'
import { Decimal } from 'decimal.js-light'
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'

import Button, { ButtonVariant } from '@/components/atomic/button'
import { ButtonSize } from '@/components/atomic/button/variants'
import { isValidTargetBand } from '@/components/molecules/constraint-list/helpers'
import { DEFAULT_TARGET_BAND_STATE } from '@/components/pages/model-builder/default-values'
import { TargetBandMode } from '@/types/enums/target-band.mode'
import { ValueUnit } from '@/types/enums/value-unit'

import TargetBandInput, {
  BufferMinMaxContainer,
  TargetBandContainer,
  TargetBandInputType,
  TargetBandSlot,
  TargetInputContainer,
} from './styles'
import {
  evaluate,
  formatValueByPrecision,
  getMinMaxFromBuffer,
  getUnitDisplay,
  MoodysRatings,
  SpRatings,
  TargetBandOperator,
} from './utils'

export enum TargetBandInputsOrder {
  TargetFirst = 'target-first',
  MinFirst = 'min-first',
}

export interface TargetBandState {
  target: string
  min: string
  max: string
  buffer: string
  mode: TargetBandMode
  unit?: ValueUnit
}

export enum TargetBandType {
  Numeric = 'numeric',
  Select = 'select',
  MoodysSelect = 'moodys-select',
}

/**
 * Check if a TargetBandType is a rating type (Select or MoodysSelect)
 * @param type The TargetBandType to check
 * @returns True if the type is a rating type
 */
const isRatingBandType = (type: TargetBandType): boolean => {
  return type === TargetBandType.Select || type === TargetBandType.MoodysSelect
}

export interface TargetBandRef {
  setTargetBandState: (state: TargetBandState) => void
}

interface TargetBandProps {
  initial?: TargetBandState | null
  type?: TargetBandType
  onChange: (state: TargetBandState) => void
  hideModeButton?: boolean
  inputsOrder?: TargetBandInputsOrder
  unit?: ValueUnit
  error?: FieldError
  // When true, entering a numeric target will auto-populate min and max as 0.8x and 1.2x of target if empty
  autoDefaultMinMax?: boolean
}

export const TargetBand = forwardRef<TargetBandRef, TargetBandProps>(
  (
    {
      initial,
      type = TargetBandType.Numeric,
      onChange,
      hideModeButton,
      inputsOrder = TargetBandInputsOrder.TargetFirst,
      unit = ValueUnit.Percentage, // Default to percentage
      // TODO: implement Zod validation errors instead of using boilerplate below
      error: _error,
      autoDefaultMinMax = false,
    },
    ref,
  ) => {
    const initialState = useMemo(() => {
      if (initial) {
        return { ...initial, unit: initial.unit ?? unit }
      }
      return { ...DEFAULT_TARGET_BAND_STATE, unit }
    }, [initial, unit])

    const [targetBandState, setTargetBandState] =
      useState<TargetBandState>(initialState)

    // Update state when unit prop changes
    useEffect(() => {
      if (targetBandState.unit !== unit) {
        setTargetBandState((prev) => ({ ...prev, unit }))
      }
    }, [unit, targetBandState.unit])

    useImperativeHandle(
      ref,
      () => ({
        setTargetBandState: (state: TargetBandState) =>
          setTargetBandState({ ...state, unit: state.unit ?? unit }),
      }),
      [unit],
    )

    const inputType =
      type === TargetBandType.Numeric
        ? TargetBandInputType.Text
        : TargetBandInputType.Select

    // Get the appropriate ratings based on target band type
    const getRatingsForType = useCallback(() => {
      switch (type) {
        case TargetBandType.Select:
          return SpRatings
        case TargetBandType.MoodysSelect:
          return MoodysRatings
        default:
          return [] // Numeric type doesn't need ratings
      }
    }, [type])

    const ratingsOptions = getRatingsForType()

    const showBuffer =
      targetBandState.mode === TargetBandMode.Buffer &&
      type === TargetBandType.Numeric

    // Get unit display for inputs (%, $, yrs)
    const unitDisplay = useMemo(
      () => getUnitDisplay(targetBandState.unit || ValueUnit.Percentage),
      [targetBandState.unit],
    )

    const handleTargetChange = useCallback(
      (value: string) => {
        // For rating types, skip numeric formatting and operations
        const isRatingType = isRatingBandType(type)

        // Format the value with appropriate precision (only for numeric types)
        const formattedValue = isRatingType
          ? value
          : formatValueByPrecision(value, targetBandState.unit)

        const updatedState = (() => {
          // For rating types, just update the target value
          if (isRatingType) {
            return { ...targetBandState, target: formattedValue }
          }

          if (
            targetBandState.mode === TargetBandMode.Buffer &&
            type === TargetBandType.Numeric
          ) {
            // Always set buffer to 20% of target
            const bufferValue = new Decimal(Number(formattedValue))
              .mul(0.2)
              .toString()

            const target = new Decimal(Number(formattedValue))
            const [min, max] = getMinMaxFromBuffer(
              formattedValue,
              bufferValue,
              targetBandState.unit,
            )
            return {
              ...targetBandState,
              target: target.toString(),
              buffer: bufferValue,
              // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
              min: min!,
              // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
              max: max!,
            }
          }

          // Auto-set min/max to 100 when target is set to 100
          const numericValue = Number(formattedValue)
          const isTarget100 = numericValue === 100

          if (
            isTarget100 &&
            type === TargetBandType.Numeric &&
            targetBandState.unit === ValueUnit.Percentage
          ) {
            if (targetBandState.mode === TargetBandMode.MinMax) {
              // In MinMax mode, set min and max to 100
              return {
                ...targetBandState,
                target: formattedValue,
                min: '100',
                max: '100',
              }
            } else if (targetBandState.mode === TargetBandMode.Buffer) {
              // In Buffer mode, set buffer to 0 so min/max will equal target
              return {
                ...targetBandState,
                target: formattedValue,
                buffer: '0',
                min: '100',
                max: '100',
              }
            }
          }

          // Default min/max based on target when first entered for numeric inputs
          if (
            autoDefaultMinMax &&
            type === TargetBandType.Numeric &&
            formattedValue !== '' &&
            // biome-ignore lint/suspicious/noGlobalIsNan: Bulk suppression as part of biome migration
            !isNaN(numericValue)
          ) {
            const isMinEmpty = !targetBandState.min
            const isMaxEmpty = !targetBandState.max
            if (isMinEmpty || isMaxEmpty) {
              const targetDecimal = new Decimal(numericValue)
              const defaultMin = formatValueByPrecision(
                targetDecimal.mul(0.9).toString(),
                targetBandState.unit,
              )
              const defaultMax = formatValueByPrecision(
                targetDecimal.mul(1.1).toString(),
                targetBandState.unit,
              )
              return {
                ...targetBandState,
                target: formattedValue,
                min: isMinEmpty ? defaultMin : targetBandState.min,
                max: isMaxEmpty ? defaultMax : targetBandState.max,
              }
            }
          }

          return { ...targetBandState, target: formattedValue }
        })()

        onChange(updatedState)
        setTargetBandState(updatedState)
      },
      [onChange, type, targetBandState, autoDefaultMinMax],
    )

    const handleMinChange = useCallback(
      (value: string) => {
        // For rating types, skip numeric formatting
        const isRatingType = isRatingBandType(type)
        const formattedValue = isRatingType
          ? value
          : formatValueByPrecision(value, targetBandState.unit)

        const updatedState = { ...targetBandState, min: formattedValue }

        onChange(updatedState)
        setTargetBandState(updatedState)
      },
      [onChange, type, targetBandState],
    )

    const handleMaxChange = useCallback(
      (value: string) => {
        // For rating types, skip numeric formatting
        const isRatingType = isRatingBandType(type)
        const formattedValue = isRatingType
          ? value
          : formatValueByPrecision(value, targetBandState.unit)

        const updatedState = { ...targetBandState, max: formattedValue }

        onChange(updatedState)
        setTargetBandState(updatedState)
      },
      [onChange, type, targetBandState],
    )

    const handlerBufferChange = useCallback(
      (value: string) => {
        // Format using the target's unit
        const formattedValue = formatValueByPrecision(
          value,
          targetBandState.unit, // Use the same unit as target
        )

        const updatedState = (() => {
          // nothing to calculate the buffer against if there's no target
          if (!targetBandState.target) return targetBandState

          const [min, max] = getMinMaxFromBuffer(
            targetBandState.target,
            formattedValue,
            targetBandState.unit,
          )

          const buffer = new Decimal(Number(formattedValue)).toString()

          return {
            ...targetBandState,
            // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
            min: buffer === '0' ? '' : min!,
            // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
            max: buffer === '0' ? '' : max!,
            buffer: buffer === '0' ? '' : buffer,
          }
        })()

        onChange(updatedState)
        setTargetBandState(updatedState)
      },
      [onChange, targetBandState],
    )

    const handleSetMode = useCallback(() => {
      const updatedState = (() => {
        const { mode } = targetBandState

        if (mode === TargetBandMode.MinMax) {
          if (!targetBandState.buffer) {
            return { ...targetBandState, mode: TargetBandMode.Buffer }
          }
          const [min, max] = getMinMaxFromBuffer(
            targetBandState.target,
            targetBandState.buffer,
            targetBandState.unit,
          )
          return {
            ...targetBandState,
            mode: TargetBandMode.Buffer,
            // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
            min: targetBandState.min || min!,
            // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
            max: targetBandState.max || max!,
          }
        }

        return {
          ...targetBandState,
          mode: TargetBandMode.MinMax,
        }
      })()

      onChange(updatedState)
      setTargetBandState(updatedState)
    }, [onChange, targetBandState])

    // The shared constraint for checking if we have a valid target band
    const needsMinMaxOrBufferConstraint = useMemo(
      () => ({
        condition:
          !targetBandState.target || isValidTargetBand(targetBandState),
        message: 'Must specify buffer, min, or max',
      }),
      [targetBandState],
    )

    const targetConstraints = useMemo(() => {
      // For buffer mode, there's no need to check min/max
      if (
        targetBandState.mode === TargetBandMode.Buffer &&
        targetBandState.target &&
        targetBandState.buffer
      ) {
        return []
      }

      // For min/max mode, use the stored values
      return [
        {
          condition: evaluate(
            targetBandState.target,
            TargetBandOperator.GreaterThanOrEqualTo,
            targetBandState.min,
          ),
          message: 'Target must be greater than or equal to min',
        },
        {
          condition: evaluate(
            targetBandState.target,
            TargetBandOperator.LessThanOrEqualTo,
            targetBandState.max,
          ),
          message: 'Target must be less than or equal to max',
        },
      ]
    }, [targetBandState])

    const minConstraints = useMemo(() => {
      const constraints = [
        {
          condition: evaluate(
            targetBandState.min,
            TargetBandOperator.LessThanOrEqualTo,
            targetBandState.max,
          ),
          message: 'Min must be less than or equal to max',
        },
        {
          condition: evaluate(
            targetBandState.min,
            TargetBandOperator.LessThanOrEqualTo,
            targetBandState.target,
          ),
          message: 'Min must be less than or equal to target',
        },
      ]

      constraints.push(needsMinMaxOrBufferConstraint)

      return constraints
    }, [targetBandState, needsMinMaxOrBufferConstraint])

    const maxConstraints = useMemo(() => {
      const constraints = [
        {
          condition: evaluate(
            targetBandState.max,
            TargetBandOperator.GreaterThanOrEqualTo,
            targetBandState.min,
          ),
          message: 'Max must be greater than or equal to min',
        },
        {
          condition: evaluate(
            targetBandState.max,
            TargetBandOperator.GreaterThanOrEqualTo,
            targetBandState.target,
          ),
          message: 'Max must be greater than or equal to target',
        },
      ]

      return constraints
    }, [targetBandState])

    const bufferConstraints = useMemo(() => {
      return showBuffer ? [needsMinMaxOrBufferConstraint] : []
    }, [showBuffer, needsMinMaxOrBufferConstraint])

    const onTargetChangeForTargetFirst = useCallback(
      (value: string) => {
        handleTargetChange(value)
        if (type === TargetBandType.MoodysSelect) {
          const ratings = Object.values(MoodysRating)
          const ratingIndex = ratings.findIndex((r) => r.toString() === value)
          setTargetBandState((prev) => {
            const below =
              ratingIndex === ratings.length - 1
                ? // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
                  ratings[ratingIndex]!
                : // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
                  ratings[ratingIndex + 1]!
            const above =
              ratingIndex === 0
                ? // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
                  ratings[ratingIndex]!
                : // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
                  ratings[ratingIndex - 1]!
            const updatedState = {
              ...prev,
              min: below,
              max: above,
            }
            onChange(updatedState)
            return updatedState
          })
        } else if (inputType === TargetBandInputType.Select) {
          // For S&P rating (select), default min to one rating below and max to one rating above target
          const ratings = Object.values(SPRating)
          const ratingIndex = ratings.findIndex((r) => r.toString() === value)
          setTargetBandState((prev) => {
            const below =
              ratingIndex === ratings.length - 1
                ? // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
                  ratings[ratingIndex]!
                : // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
                  ratings[ratingIndex + 1]!
            const above =
              ratingIndex === 0
                ? // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
                  ratings[ratingIndex]!
                : // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
                  ratings[ratingIndex - 1]!
            const updatedState = {
              ...prev,
              min: below,
              max: above,
            }
            onChange(updatedState)
            return updatedState
          })
        }
      },
      [handleTargetChange, inputType, onChange, type],
    )

    return (
      <TargetBandContainer>
        {inputsOrder === TargetBandInputsOrder.TargetFirst && (
          <TargetInputContainer>
            <TargetBandInput
              type={inputType}
              slot={TargetBandSlot.Target}
              options={ratingsOptions}
              constraints={targetConstraints}
              value={targetBandState.target}
              unitDisplay={unitDisplay} // Pass the unit display
              onChange={onTargetChangeForTargetFirst}
            />
          </TargetInputContainer>
        )}
        <BufferMinMaxContainer halfWidth={showBuffer}>
          {showBuffer && (
            <TargetBandInput
              className="w-full"
              disabled={!targetBandState.target}
              type={inputType}
              slot={TargetBandSlot.Buffer}
              placeholder={
                !targetBandState.target
                  ? 'Enter target before buffer'
                  : 'Enter buffer'
              }
              value={targetBandState.buffer}
              onChange={handlerBufferChange}
              unitDisplay={unitDisplay}
              constraints={bufferConstraints}
            />
          )}
          {!showBuffer && (
            // biome-ignore lint/complexity/noUselessFragments: Bulk suppression as part of biome migration
            <>
              <TargetBandInput
                type={inputType}
                slot={TargetBandSlot.Min}
                options={ratingsOptions}
                placeholder="Select min"
                constraints={minConstraints}
                value={targetBandState.min}
                onChange={handleMinChange}
                unitDisplay={unitDisplay}
              />
            </>
          )}
          {inputsOrder === TargetBandInputsOrder.MinFirst && (
            <TargetInputContainer>
              <TargetBandInput
                type={inputType}
                slot={TargetBandSlot.Target}
                options={ratingsOptions}
                constraints={targetConstraints}
                value={targetBandState.target}
                onChange={handleTargetChange}
                unitDisplay={unitDisplay}
              />
            </TargetInputContainer>
          )}
          {!showBuffer && (
            // biome-ignore lint/complexity/noUselessFragments: Bulk suppression as part of biome migration
            <>
              <TargetBandInput
                type={inputType}
                slot={TargetBandSlot.Max}
                options={ratingsOptions}
                placeholder="Select max"
                constraints={maxConstraints}
                value={targetBandState.max}
                onChange={handleMaxChange}
                unitDisplay={unitDisplay}
              />
            </>
          )}
          {!isRatingBandType(type) && !hideModeButton && (
            <Button
              className="w-fit"
              variant={ButtonVariant.Secondary}
              label={`Set ${targetBandState.mode === TargetBandMode.Buffer ? 'min/max' : 'buffer'}`}
              size={ButtonSize.Small}
              onClick={handleSetMode}
            />
          )}
        </BufferMinMaxContainer>
      </TargetBandContainer>
    )
  },
)

TargetBand.displayName = 'TargetBand'
